import React, { useState } from "react";
import useLockBodyScroll from "../hooks/useLockBodyScroll.js";
import CustomSelect from "./CustomSelect.jsx";

const FORMSPREE_URL = "https://formspree.io/f/xkgzrvav";
const MIN_HUMAN_DELAY_MS = 2000; // submissions faster than this look like bots

function normalizeUrl(raw) {
  if (!raw) return "";
  let v = raw.trim();
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  return v;
}

function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export default function HealthCheckCTA({
  buttonLabel = "Free Website Health Check",
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openedAt, setOpenedAt] = useState(null);

  useLockBodyScroll(isOpen);

  const open = () => {
    setIsOpen(true);
    setSubmitted(false);
    setErr("");
    setOpenedAt(Date.now());
  };

  const close = () => {
    setIsOpen(false);
    setSubmitted(false);
    setErr("");
    setOpenedAt(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());

    // ---- Anti-spam checks ----
    // 1) Honeypot: if _gotcha is filled, silently "succeed"
    if (payload._gotcha && payload._gotcha.trim() !== "") {
      setSubmitted(true);
      setLoading(false);
      setTimeout(close, 2000);
      return;
    }

    // 2) Human-speed check: if it's submitted too fast after open, silently "succeed"
    if (openedAt && Date.now() - openedAt < MIN_HUMAN_DELAY_MS) {
      setSubmitted(true);
      setLoading(false);
      setTimeout(close, 2000);
      return;
    }
    // --------------------------

    // Normalise URL
    payload.website = normalizeUrl(payload.website);
    if (!isValidUrl(payload.website)) {
      setLoading(false);
      setErr("Please enter a valid website URL (e.g. https://example.com)");
      return;
    }

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSubmitted(true);
        setTimeout(close, 2000);
      } else {
        throw new Error("Submission failed");
      }
    } catch (e) {
      console.error(e);
      setErr("Something went wrong submitting the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={open}
        className={`bg-orange-500 text-white px-5 py-3 rounded-full shadow-lg text-sm sm:text-base md:text-lg hover:bg-orange-600 transition ${className}`}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2147483647]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
            <button
              onClick={close}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl"
              aria-label="Close"
            >
              &times;
            </button>

            {!submitted ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-slate-900">
                  Free Website Health Check
                </h3>

                {err && (
                  <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                    {err}
                  </div>
                )}

                <form className="space-y-4" onSubmit={onSubmit}>
                  {/* Honeypot (hidden from users, bots often fill it) */}
                  <input
                    type="text"
                    name="_gotcha"
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ display: "none" }}
                  />

                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    required
                    className="w-full border border-gray-300 rounded p-2"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    className="w-full border border-gray-300 rounded p-2"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    required
                    className="w-full border border-gray-300 rounded p-2"
                  />
                  <input
                    type="text"
                    name="website"
                    placeholder="Website URL"
                    required
                    className="w-full border border-gray-300 rounded p-2"
                  />

                  <CustomSelect
                    name="severity"
                    defaultValue="ok"
                    options={[
                      { value: "ok", label: "My website is OK" },
                      { value: "bad", label: "My website is Bad" },
                      { value: "terrible", label: "My website is Terrible" },
                    ]}
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition disabled:opacity-70"
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <h3 className="text-lg font-semibold mb-2 text-green-600">
                  Thanks! 🎉
                </h3>
                <p className="text-slate-600">
                  We’ve received your details. We’ll be in touch shortly.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
